import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import c from 'classnames';
import { toast } from 'react-toastify';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useProfileQuestions from 'services/general/profileQuestions';
import useSampleInfoQuestions from 'services/surveys/useSampleInfoQuestions';
import useUpdateProfileAnswer from 'services/surveys/useUpdateProfileAnswer';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useUrlQuery } from 'hooks/useUrlQuery';
import {
  HealthFormBundle,
  HealthFormBundleKey,
  useHealthFormsSequence,
} from 'hooks/useHealthFormsSequence';
import useFeature from 'services/features/useFeature';
import {
  ProfileQuestion,
  ProfileQuestionAnswer,
  ProfileQuestionsGroup,
} from 'types/ProfileQuestions';

import {
  ActivationScreen,
  ButtonColors,
  ButtonSizes,
  ContainerSizes,
  DesignButton,
  Header,
  HealthFormTransition,
  PageContainer,
} from 'components';
import { ProfileConfirmationGroup } from './ProfileConfirmationGroup';

import styles from './ProfileConfirmation.module.css';

// Same fake-loader delay used between bundles in Profile.tsx's THSurvey.
const NEXT_BUNDLE_TRANSITION_DELAY_MS = 6000;

export type ProfileFormData = {
  [key: string]: number | string | string[];
};
export type ProfileActionVariant = 'view' | 'edit' | 'confirm';
export type ProfileFlowType = 'sampleinfo' | 'profile';

const ProfileConfirmation = ({
  variant,
}: {
  variant?: ProfileActionVariant;
}) => {
  const flowType = useUrlQuery().get('flow');
  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();

  if (currentKitId && currentTinyAccountId) {
    return flowType === 'sampleinfo' ? (
      <SampleInfoFlow
        tinyAccountId={currentTinyAccountId}
        kitId={currentKitId}
        variant={variant}
      />
    ) : (
      <ProfileFlow
        tinyAccountId={currentTinyAccountId}
        kitId={currentKitId}
        variant={variant}
      />
    );
  }
  return <></>;
};

const ProfileFlow = ({
  tinyAccountId,
  kitId,
  variant,
}: {
  tinyAccountId: string;
  kitId: string;
  variant?: ProfileActionVariant;
}) => {
  const { data: profileQuestions } = useProfileQuestions(tinyAccountId, kitId);
  const profileAnswerMutation = useUpdateProfileAnswer();

  const mutationCallback = useCallback(
    (answers: ProfileQuestionAnswer[]) => {
      return profileAnswerMutation.mutateAsync({
        answers,
        tinyAccountId,
        kitId,
      });
    },
    [profileAnswerMutation],
  );

  if (!tinyAccountId) return <></>;
  return (
    <THSurveyConfirmation
      questionGroups={profileQuestions}
      mutationCallback={mutationCallback}
      kitId={kitId}
      tinyAccountId={tinyAccountId}
      variant={variant}
      flow='profile'
    />
  );
};

const SampleInfoFlow = ({
  tinyAccountId,
  kitId,
  variant,
}: {
  tinyAccountId: string;
  kitId: string;
  variant?: ProfileActionVariant;
}) => {
  const { data: sampleInfoQuestions } = useSampleInfoQuestions(
    tinyAccountId,
    kitId,
  );
  const profileAnswerMutation = useUpdateProfileAnswer();

  const mutationCallback = useCallback(
    (answers: ProfileQuestionAnswer[]) => {
      return profileAnswerMutation.mutateAsync({
        answers,
        tinyAccountId,
        kitId,
      });
    },
    [profileAnswerMutation],
  );

  if (!tinyAccountId) return <></>;
  return (
    <THSurveyConfirmation
      questionGroups={sampleInfoQuestions}
      mutationCallback={mutationCallback}
      kitId={kitId}
      tinyAccountId={tinyAccountId}
      variant={variant}
      flow='sampleinfo'
    />
  );
};

const THSurveyConfirmation = ({
  questionGroups,
  mutationCallback,
  kitId,
  tinyAccountId,
  variant,
  flow,
}: {
  questionGroups?: ProfileQuestionsGroup[] | null;
  mutationCallback?: (answers: ProfileQuestionAnswer[]) => Promise<void>;
  kitId: string;
  tinyAccountId: string;
  variant?: ProfileActionVariant;
  flow: ProfileFlowType;
}) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextBundleToShow, setNextBundleToShow] =
    useState<HealthFormBundle | null>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { trackEvent } = useAnalytics();
  const { isFeatureEnabled: isRedesignedActivationEnabled } = useFeature(
    'redesigned_kit_activation_flow',
  );
  const { bundles } = useHealthFormsSequence(tinyAccountId, kitId);
  const bundleKey: HealthFormBundleKey =
    flow === 'sampleinfo' ? 'sampleInfo' : 'profile';

  useEffect(() => {
    return () => clearTimeout(transitionTimeoutRef.current);
  }, []);

  const onSubmit = async () => {
    setIsSubmitting(true);
    const allQuestions =
      questionGroups?.reduce<ProfileQuestion[]>(
        (
          accumulator: ProfileQuestion[],
          currentGroup: ProfileQuestionsGroup,
        ) => {
          return accumulator.concat(currentGroup.questions);
        },
        [],
      ) ?? [];
    const answers = allQuestions?.map(question => {
      return { question_id: question.id, answer_text: question.answer_text };
    });
    if (mutationCallback) {
      try {
        await mutationCallback(answers);
        if (variant === 'confirm') {
          trackEvent('Health Story Survey Completed', {
            section: flow === 'sampleinfo' ? 'sample_info' : 'profile',
          });
        }
        if (isRedesignedActivationEnabled) {
          const bundleIndex = bundles.findIndex(
            bundle => bundle.key === bundleKey,
          );
          const nextBundle = bundles
            .slice(bundleIndex + 1)
            .find(bundle => !bundle.done);
          if (nextBundle) {
            setNextBundleToShow(nextBundle);
            transitionTimeoutRef.current = setTimeout(() => {
              navigate(nextBundle.href, { replace: true });
            }, NEXT_BUNDLE_TRANSITION_DELAY_MS);
          } else {
            navigate(`/information/${kitId}/complete`);
          }
        } else {
          navigate(state?.from ? state.from : `/`);
        }
        if (!isRedesignedActivationEnabled) {
          toast.success('Answers saved successfully');
        }
      } catch (error) {
        toast.error('Something went wrong, please try again');
      }
    }
    setIsSubmitting(false);
  };

  const headerTitle =
    variant === 'view'
      ? 'View your answers'
      : variant === 'confirm'
        ? 'Review your previous answers'
        : 'Edit your answers';

  const goBack = () => {
    if (state?.from) {
      navigate(state.from);
    } else if ((window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  if (!questionGroups?.length) return <></>;

  if (isRedesignedActivationEnabled) {
    return (
      <ActivationScreen
        onBack={nextBundleToShow ? undefined : goBack}
        header={
          nextBundleToShow ? null : (
            <div className={styles.activationHeader}>
              <Header title={headerTitle} />
            </div>
          )
        }
        footer={
          variant === 'confirm' &&
          !nextBundleToShow && (
            <DesignButton
              label='Continue'
              loading={isSubmitting}
              size={ButtonSizes.M}
              color={ButtonColors.PURPLE}
              width={'full'}
              onClick={onSubmit}
            />
          )
        }
      >
        <PageContainer
          size={ContainerSizes.SM}
          className={styles.activationBody}
        >
          {nextBundleToShow ? (
            <HealthFormTransition
              nextLabel={nextBundleToShow.label}
              nextTimeEstimate={nextBundleToShow.timeEstimate}
              nextDescription={nextBundleToShow.description}
            />
          ) : (
            <div
              className={c(
                styles.activationGroups,
                variant !== 'confirm' && styles.activationGroupsNoFooter,
              )}
            >
              {questionGroups.map(questionGroup => (
                <ProfileConfirmationGroup
                  key={questionGroup.id}
                  questionGroup={questionGroup}
                  variant={variant}
                  flow={flow}
                />
              ))}
            </div>
          )}
        </PageContainer>
      </ActivationScreen>
    );
  }

  return (
    <>
      <Header title={headerTitle} />
      <PageContainer
        size={ContainerSizes.SM}
        className={styles.profileContainer}
      >
        {nextBundleToShow ? (
          <HealthFormTransition
            nextLabel={nextBundleToShow.label}
            nextTimeEstimate={nextBundleToShow.timeEstimate}
            nextDescription={nextBundleToShow.description}
          />
        ) : (
          <div className={styles.groupsContainer}>
            {questionGroups.map(questionGroup => (
              <ProfileConfirmationGroup
                key={questionGroup.id}
                questionGroup={questionGroup}
                variant={variant}
                flow={flow}
              />
            ))}
            <div className={styles.buttons}>
              {variant === 'confirm' && (
                <DesignButton
                  label='Continue'
                  loading={isSubmitting}
                  size={ButtonSizes.M}
                  color={ButtonColors.PURPLE}
                  width={'full'}
                  className='mt-2'
                  onClick={onSubmit}
                />
              )}
            </div>
          </div>
        )}
      </PageContainer>
    </>
  );
};

export default ProfileConfirmation;
